import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Colors } from '@/common/colors/colors';
import { round } from '../helper/round';

const TableContainer = styled.div`
	margin-bottom: 2rem;
`;

const Title = styled.h3`
	color: ${(props) => props.$color || Colors.darkBlue};
	font-size: 18px;
	margin-bottom: 15px;
`;

const TableWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
	border: 1px solid ${Colors.gray};
	border-radius: 4px;
`;

const StyledTable = styled.table`
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	th,
	td {
		padding: 10px 14px;
		text-align: center;
		font-size: 13px;
		border-bottom: 1px solid ${Colors.gray};
		border-right: 1px solid ${Colors.gray};
		white-space: nowrap;
	}

	th {
		background-color: ${Colors.darkBlue};
		color: white;
		font-weight: 600;
	}

	th:first-child,
	td:first-child {
		position: sticky;
		left: 0;
		z-index: 20;
		text-align: left;
		border-right: 2px solid ${Colors.gray};
		background-color: #f8f9fa;
	}

	th:last-child,
	td:last-child {
		position: sticky;
		right: 0;
		z-index: 20;
		border-left: 2px solid ${Colors.gray};
	}

	th:first-child {
		background-color: ${Colors.darkBlue};
	}
	th:last-child {
		background-color: #1a3a6b;
	}

	tr:last-child td {
		font-weight: bold;
		border-bottom: none;
	}
`;

const KpiCell = styled.td`
	background-color: ${(props) =>
		props.$bgColor ? `${props.$bgColor} !important` : 'white'};
`;

const formatKpi = (value) => (value != null ? Number(value) : '—');

const getKpiColor = (value, threshold) => {
	if (value == null) {
		return undefined;
	}

	const diff = round(value - threshold);
	const intensity = Math.min(Math.floor(Math.abs(diff) / 0.2), 5);
	const lightness = 85 - intensity * 5;

	return diff >= 0
		? `hsl(142, 60%, ${lightness}%)`
		: `hsl(355, 80%, ${lightness}%)`;
};

const EmployeeKpiTable = ({
	title,
	rows = [],
	dates = [],
	kpiKey,
	countKey,
	threshold,
}) => {
	const { tableData, totalsByDate, grandTotalKpi } = useMemo(() => {
		const dateTotals = Object.fromEntries(
			dates.map((d) => [d, { count: 0, hours: 0 }]),
		);
		let grandCount = 0;
		let grandHours = 0;

		const processedRows = rows.map((row) => {
			let rowCount = 0;
			let rowHours = 0;
			const userDates = {};

			row.dates.forEach((d) => {
				userDates[d.date] = d;
				if (d.hours != null) {
					const c = d[countKey] || 0;
					rowCount += c;
					rowHours += d.hours;
					if (dateTotals[d.date]) {
						dateTotals[d.date].count += c;
						dateTotals[d.date].hours += d.hours;
					}
					grandCount += c;
					grandHours += d.hours;
				}
			});

			return {
				...row,
				userDates,
				periodKpi: rowHours ? round(rowCount / rowHours) : null,
			};
		});

		const calculatedTotalsByDate = dates.map((d) =>
			dateTotals[d].hours
				? round(dateTotals[d].count / dateTotals[d].hours)
				: null,
		);

		const calculatedGrandTotalKpi = grandHours
			? round(grandCount / grandHours)
			: null;

		return {
			tableData: processedRows,
			totalsByDate: calculatedTotalsByDate,
			grandTotalKpi: calculatedGrandTotalKpi,
		};
	}, [rows, dates, countKey]);

	return (
		<TableContainer>
			<Title $color={Colors.darkBlue}>{title}</Title>
			<TableWrapper>
				<StyledTable>
					<thead>
						<tr>
							<th>Consultant</th>
							{dates.map((date) => (
								<th key={date}>{date}</th>
							))}
							<th>Period</th>
						</tr>
					</thead>
					<tbody>
						{tableData.map((row) => (
							<tr key={row.userId}>
								<td>{row.username}</td>
								{dates.map((date) => {
									const val = row.userDates[date]?.[kpiKey];
									return (
										<KpiCell key={date} $bgColor={getKpiColor(val, threshold)}>
											{formatKpi(val)}
										</KpiCell>
									);
								})}
								<KpiCell $bgColor={getKpiColor(row.periodKpi, threshold)}>
									{formatKpi(row.periodKpi)}
								</KpiCell>
							</tr>
						))}
						<tr>
							<td style={{ backgroundColor: '#eee' }}>SUM</td>
							{totalsByDate.map((total, idx) => (
								<KpiCell key={idx} $bgColor={getKpiColor(total, threshold)}>
									{formatKpi(total)}
								</KpiCell>
							))}
							<KpiCell $bgColor={getKpiColor(grandTotalKpi, threshold)}>
								{formatKpi(grandTotalKpi)}
							</KpiCell>
						</tr>
					</tbody>
				</StyledTable>
			</TableWrapper>
		</TableContainer>
	);
};

export default EmployeeKpiTable;
